import { useEffect, useState, useMemo } from "react";
import parse from "html-react-parser";
import { useThesaurusArea, useThesaurusChildren, useThesaurusTerm } from "@/hooks/api/useThesaurus";
import { usePhotos, useDocuments } from "@/hooks/api/useDocuments";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router";
import { useTreeData } from "@/contexts/treeDataContext";
import { getImageUrl } from "@/utils/dedaloUtils";
import Layout from "@/components/Layout/Layout";
import Link from "@/components/LangLink/LangLink";
import Loading from "@/components/Loading/Loading";
import Links from "@/components/Resources/Links";
import Photos from "@/components/Resources/Photos";
import Videos from "@/components/Resources/Videos";
import Documents from "@/components/Resources/Documents";
import Bibliography from "@/components/Resources/Bibliography";

export default function Area({ id, termId, parent, slug }) {
    const { t } = useTranslation();
    const { data: parentData } = useThesaurusTerm(parent);
    const { data, isLoading, error } = useThesaurusArea(id);
    const { data: childrenData, isLoading: childrenLoading, error: childrenError } = useThesaurusChildren(termId);
    const { data: biografiasData } = useThesaurusChildren(childrenData?.find((child) => child.web_path === 'biografias')?.term_id || '');
    const documents_indexation_ids = JSON.parse(data?.[0]?.documents_indexation || '[]').join();
    const { data: photosData, isLoading: photosLoading, error: photosError } = usePhotos(documents_indexation_ids || '00');
    const { data: documentsData, isLoading: documentsLoading, error: documentsError } = useDocuments(documents_indexation_ids || '00');
    const index_locators = data?.[0]?.indexations ? JSON.parse(data[0].indexations) : [];
    const [ userExpanded, setUserExpanded ] = useState(false);
    const { treeData } = useTreeData();
    const [searchParams, setSearchParams] = useSearchParams();

    const openResourceType = searchParams.get('recursos') || null;
    const parentTerm = parentData?.[0]?.term.split(':') || [];
    const parentWebPath = parentData?.[0]?.web_path || '';
    const sectionInfo = {title: parentTerm[0], description: parentTerm[1], slug: parentWebPath};
    const term = data?.[0]?.term || '';
    const abstract = data?.[0]?.abstract || '';
    const body = data?.[0]?.body || '';
    const bodyWithHighlights = body.replaceAll('<strong>', '<p class="highlighted-p-1">').replaceAll('</strong>', '</p>');
    const imageUrl = data?.[0]?.image?.[0]?.image || '';
    const imageFootprint = data?.[0]?.image?.[0]?.footprint || '';
    const prevPath = treeData.pathOrder[treeData.pathOrder.indexOf(slug) - 1];
    const nextPath = treeData.pathOrder[treeData.pathOrder.indexOf(slug) + 1];
    const children = childrenData?.filter((child) => child.web_path !== 'biografias');
    const enlaces = data?.[0]?.uri || null;
    const bibliografia = data?.[0]?.bibliography_data || null;
    const photos = photosData?.filter(photo => photo.identify_image) || [];
    const documents = documentsData?.filter(doc => doc.identify_image) || [];
    const hasResources = photos?.length > 0 || documents?.length > 0 || index_locators?.length > 0 || enlaces || bibliografia?.length > 0;
    const location = useLocation();

    const randLocators = useMemo(() => {
        const arr = [...index_locators];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    }, [data])

    useEffect(() => {
        setUserExpanded(false);
    }, [location.pathname])

    function resourceClickHandler(e) {
        const resourceType = e.currentTarget.dataset.resource;
        const p = new URLSearchParams(searchParams);
        p.set('recursos', resourceType);
        setSearchParams(p);
    }

    function closeResource() {
        const p = new URLSearchParams(searchParams);
        p.delete('recursos');
        setSearchParams(p);
    }

    if (isLoading || childrenLoading || photosLoading || documentsLoading) return <Layout><Loading /></Layout>
    if (error || childrenError || photosError || documentsError) return <Layout><Loading error={true}/></Layout>

    if (openResourceType === 'enlaces') return <Links section={sectionInfo} data={data} close={closeResource} />;
    if (openResourceType === 'fotos') return <Photos section={sectionInfo} data={data} photos={photos} close={closeResource} />;
    if (openResourceType === 'audiovisuales') return <Videos section={sectionInfo} data={data} index_locators={randLocators} close={closeResource} />;
    if (openResourceType === 'documentos') return <Documents section={sectionInfo} data={data} documents={documents} close={closeResource} />;
    if (openResourceType === 'bibliografia') return <Bibliography section={sectionInfo} data={data} close={closeResource} />;

    return (
        <Layout section={sectionInfo}>
            <div className="area-card has-background-light bottom-shadow py-7">
                <div className="wrapper">
                        <div className="columns gap-8 is-widescreen pt-0">
                            <div className="column is-one-quarter-widescreen is-hidden-mobile is-relative">
                                <div className="area-card--image-container">
                                    <figure>
                                        <img src={getImageUrl(imageUrl)} alt="" />
                                        <figcaption className="has-text-white is-size-4 px-6 py-3 is-flex is-justify-content-end">{imageFootprint}</figcaption>
                                    </figure>
                                </div>
                            </div>
                            <div className="column flow--l">
                                <div className="columns gap-8">
                                    <div className="column">
                                        <div className="flow--xl">
                                            <h1 className="is-uppercase"><strong>{term}</strong></h1>
                                            <div className="is-hidden-tablet pt-7">
                                                <div className="area-card--image-container">
                                                    <figure>
                                                        <img src={getImageUrl(imageUrl)} alt="" />
                                                        <figcaption className="has-text-white is-size-4 p-3 is-flex is-justify-content-end">{imageFootprint}</figcaption>
                                                    </figure>
                                                </div>
                                            </div>
                                            <div className="flow">
                                                <div>
                                                    {abstract && <>{parse(abstract)}</>}
                                                    {body && (<>
                                                        <br/>
                                                        &nbsp;
                                                        <br/>
                                                        {userExpanded
                                                            ? (
                                                                <>{parse(bodyWithHighlights)}</>
                                                            )
                                                            : (
                                                                <button
                                                                    className="button-reset is-size-5 is-uppercase has-text-weight-semibold"
                                                                    onClick={() => {setUserExpanded(true);}}
                                                                >
                                                                    {t('Leer mas')} <span className="is-sr-only">{`${t('sobre')} ${term}`}</span>
                                                                </button>
                                                            )}
                                                    </>)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {(hasResources || (children?.length > 0) || (biografiasData && biografiasData.length > 0)) && (
                                        <div className="column flow">
                                            {hasResources && (<>
                                                <h2>{t('Recursos')}</h2>
                                                <ul className="is-flex is-flex-wrap-wrap gap">
                                                    {photos && photos.length > 0 && (
                                                        <li><button className="button button--fotos" data-resource="fotos" onClick={resourceClickHandler}>{t('Fotos')}</button></li>
                                                    )}
                                                    {documents && documents.length > 0 && (
                                                        <li><button className="button button--documentos" data-resource="documentos" onClick={resourceClickHandler}>{t('Documentos')}</button></li>
                                                    )}
                                                    {index_locators && index_locators.length > 0 && (
                                                        <li><button className="button button--audiovisuales" data-resource="audiovisuales" onClick={resourceClickHandler}>{t('Audiovisuales')}</button></li>
                                                    )}
                                                    {enlaces && (
                                                        <li><button className="button button--enlaces" data-resource="enlaces" onClick={resourceClickHandler}>{t('Enlaces')}</button></li>
                                                    )}
                                                    {bibliografia && bibliografia.length > 0 && (
                                                        <li><button className="button button--bibliografia" data-resource="bibliografia" onClick={resourceClickHandler}>{t('Bibliografia')}</button></li>
                                                    )}
                                                </ul>
                                            </>)}

                                            {children?.length > 0 && <>
                                                <h2>{t('Subambitos')}</h2>
                                                <ul className="flow--3xs sub-area-list">
                                                    {children.map((child) => (
                                                        <li key={child.term_id}>
                                                            <Link to={"/" + child.web_path} className="button is-secondary">{child.term}</Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </>}
                                            {biografiasData && biografiasData.length > 0 && <>
                                                <h2>{t('Biografias')}</h2>
                                                <ul className="bio-list">
                                                    {biografiasData.map((bio) => (
                                                        <li key={bio.term_id}><Link to={"/" + bio.web_path} className="button is-secondary">{bio.term}</Link></li>
                                                    ))}
                                                </ul>
                                            </>}
                                        </div>
                                    )}
                                </div>
                                <nav className="is-flex is-justify-content-space-between">
                                    {prevPath && (
                                        <Link to={"/" + prevPath} aria-label={`${t('Ir al apartado')} ${prevPath}`} className="mr-auto">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="146" height="14"><g fill="none" fillRule="evenodd" stroke="#000"><path d="M145.44 7H1.001M7 1 1 7l6 6"/></g></svg>
                                        </Link>
                                    )}
                                    {nextPath && (
                                        <Link to={"/" + nextPath} aria-label={`${t('Ir al apartado')} ${nextPath}`} className="ml-auto">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="146" height="14"><g fill="none" fillRule="evenodd" stroke="#000000"><path d="M0 7h144.439M138.44 1l6 6-6 6"/></g></svg>
                                        </Link>
                                    )}
                                </nav>
                            </div>
                        </div>

                </div>
            </div>
        </Layout>
    )
}